import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pageTitle } from '../utils/pageTitle';

// P2-22: `canOpen(path)` drops shortcuts to screens the viewer cannot open
const QuickActions = ({ actions, mostVisitedPages = [], canOpen = () => true }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Title in the active language; the stored title is the language of the visit.
  const getTranslatedTitle = (path, title) => pageTitle(t, path) || title;

  const actionConfig = {
    create_sales: { label: t('quickActions.createSales'), icon: '🛒', path: '/modules/sales', color: 'blue' },
    create_hr: { label: t('quickActions.createHr'), icon: '👥', path: '/users', color: 'green' },
    create_inventory: { label: t('quickActions.createInventory'), icon: '📦', path: '/modules/inventory', color: 'purple' },
    create_finance: { label: t('quickActions.createFinance'), icon: '💰', path: '/modules/finance', color: 'orange' },
    create_service: { label: t('quickActions.createService'), icon: '🔧', path: '/modules/service', color: 'indigo' },
    create_purchasing: { label: t('quickActions.createPurchasing'), icon: '🛍️', path: '/modules/purchasing', color: 'cyan' },
    create_accounting: { label: t('quickActions.createAccounting'), icon: '📒', path: '/modules/accounting', color: 'teal' },
    create_tender: { label: t('quickActions.createTender'), icon: '📋', path: '/modules/tender', color: 'amber' },
    create_crm: { label: t('quickActions.createCrm'), icon: '🤝', path: '/modules/crm', color: 'pink' },
    create_assets: { label: t('quickActions.createAssets'), icon: '🏷️', path: '/modules/assets', color: 'violet' },
    create_office: { label: t('quickActions.createOffice'), icon: '🏢', path: '/modules/office', color: 'slate' },
    create_production: { label: t('quickActions.createProduction'), icon: '🏭', path: '/modules/production', color: 'red' },
    create_company: { label: t('quickActions.createCompany'), icon: '🏢', path: '/companies', color: 'blue' },
    manage_licenses: { label: t('quickActions.manageLicenses'), icon: '🔑', path: '/licenses', color: 'yellow' },
    view_reports: { label: t('quickActions.viewReports'), icon: '📊', path: '/reports', color: 'pink' }
  };

  // Icons for most visited pages based on path
  const getPageIcon = (path) => {
    if (path.includes('sales')) return '💰';
    if (path.includes('hr') || path.includes('employee')) return '👥';
    if (path.includes('inventory') || path.includes('stock')) return '📦';
    if (path.includes('finance')) return '💵';
    if (path.includes('accounting')) return '📒';
    if (path.includes('crm')) return '🤝';
    if (path.includes('service')) return '🔧';
    if (path.includes('purchasing')) return '🛍️';
    if (path.includes('tender')) return '📋';
    if (path.includes('tasks')) return '✅';
    if (path.includes('production')) return '🏭';
    if (path.includes('assets')) return '🏷️';
    if (path.includes('office')) return '🏢';
    if (path.includes('dashboard')) return '📊';
    if (path.includes('settings')) return '⚙️';
    if (path.includes('users')) return '👤';
    return '📄';
  };

  // Color based on path
  const getPageColor = (path) => {
    if (path.includes('sales')) return 'blue';
    if (path.includes('hr')) return 'green';
    if (path.includes('inventory')) return 'purple';
    if (path.includes('finance')) return 'orange';
    if (path.includes('accounting')) return 'teal';
    if (path.includes('crm')) return 'pink';
    if (path.includes('service')) return 'indigo';
    if (path.includes('purchasing')) return 'cyan';
    if (path.includes('tender')) return 'amber';
    if (path.includes('tasks')) return 'lime';
    if (path.includes('production')) return 'red';
    if (path.includes('assets')) return 'violet';
    if (path.includes('office')) return 'slate';
    return 'gray';
  };

  const getColorClass = (color) => {
    const colors = {
      blue: 'bg-blue-500 hover:bg-blue-600',
      green: 'bg-green-500 hover:bg-green-600',
      purple: 'bg-purple-500 hover:bg-purple-600',
      orange: 'bg-orange-500 hover:bg-orange-600',
      indigo: 'bg-indigo-500 hover:bg-indigo-600',
      yellow: 'bg-yellow-500 hover:bg-yellow-600',
      pink: 'bg-pink-500 hover:bg-pink-600',
      teal: 'bg-teal-500 hover:bg-teal-600',
      cyan: 'bg-cyan-500 hover:bg-cyan-600',
      amber: 'bg-amber-500 hover:bg-amber-600',
      lime: 'bg-lime-500 hover:bg-lime-600',
      red: 'bg-red-500 hover:bg-red-600',
      violet: 'bg-violet-500 hover:bg-violet-600',
      slate: 'bg-slate-500 hover:bg-slate-600',
      gray: 'bg-gray-500 hover:bg-gray-600'
    };
    return colors[color] || colors.blue;
  };

  // Determine what to show: most visited pages or default actions
  const visitedPages = (mostVisitedPages || []).filter((page) => page?.path && canOpen(page.path));
  const hasMostVisited = visitedPages.length > 0;
  const shownActions = (actions || []).filter((action) => !actionConfig[action] || canOpen(actionConfig[action].path));

  return (
    <div className="bg-white dark:bg-surface-900 rounded-xl shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-surface-50 mb-4">
        {hasMostVisited ? `⭐ ${t('quickActions.title')}` : `⚡ ${t('quickActions.defaultTitle')}`}
      </h3>

      <div className="grid grid-cols-2 gap-3">
        {hasMostVisited ? (
          // Show most visited pages
          visitedPages.slice(0, 6).map((page, index) => (
            <button
              key={index}
              onClick={() => navigate(page.path)}
              className={`${getColorClass(getPageColor(page.path))} text-white rounded-lg p-4 text-center transition-all transform hover:scale-105 shadow-md`}
            >
              <div className="text-2xl mb-2">{getPageIcon(page.path)}</div>
              <div className="text-sm font-medium truncate">{getTranslatedTitle(page.path, page.title)}</div>
              <div className="text-xs opacity-75 mt-1">{t('sharedui.quickActions.visits', { count: page.visit_count })}</div>
            </button>
          ))
        ) : shownActions.length > 0 ? (
          // Show default quick actions
          shownActions.slice(0, 6).map((action, index) => {
            const config = actionConfig[action] || {
              label: action,
              icon: '📌',
              path: '/',
              color: 'blue'
            };

            return (
              <button
                key={index}
                onClick={() => navigate(config.path)}
                className={`${getColorClass(config.color)} text-white rounded-lg p-4 text-center transition-all transform hover:scale-105 shadow-md`}
              >
                <div className="text-2xl mb-2">{config.icon}</div>
                <div className="text-sm font-medium">{config.label}</div>
              </button>
            );
          })
        ) : (
          <div className="col-span-2 text-center text-gray-500 dark:text-surface-400 py-4">
            {t('quickActions.noActions')}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickActions;
